import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "readme-section" };
const _hoisted_2 = { class: "d-flex align-center justify-space-between mb-3" };
const _hoisted_3 = {
    key: 0,
    class: "readme-editor"
};
const _hoisted_4 = { class: "d-flex justify-end mt-2 gap-2" };
const _hoisted_5 = {
    key: 1,
    class: "readme-content"
};
const _hoisted_6 = { class: "readme-pre" };
const _hoisted_7 = ["innerHTML"];
const _hoisted_8 = {
    key: 2,
    class: "text-center py-8 text-medium-emphasis"
};
import { ref, computed, watch } from "vue";
import { useStore } from "@/store";
import { useNotificationStore } from "@/stores/notifications";
import { dserverApi } from "@/services/dserverApi";
import * as yaml from "yaml";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatasetReadme',
    setup(__props) {
        const store = useStore();
        const notifications = useNotificationStore();
        const isEditing = ref(false);
        const editedContent = ref("");
        const yamlError = ref(null);
        const isSaving = ref(false);
        const getReadmeContent = computed(() => {
            if (!store.current_dataset_readme || !store.current_dataset_readme.readme) {
                return null;
            }
            return store.current_dataset_readme.readme.trim();
        });
        const isValidYaml = computed(() => {
            if (!getReadmeContent.value)
                return false;
            try {
                yaml.parse(getReadmeContent.value);
                return true;
            }
            catch {
                return false;
            }
        });
        const formattedReadme = computed(() => {
            if (!getReadmeContent.value)
                return "";
            // Color-code YAML-like key: value pairs
            return getReadmeContent.value
                .split("\n")
                .map((line) => formatLine(line))
                .join("\n");
        });
        const edit_command = computed(() => {
            if (!store.current_dataset)
                return "";
            return "dtool readme edit " + store.current_dataset.uri;
        });
        // Reset editing state when dataset changes
        watch(() => store.current_dataset?.uri, () => {
            isEditing.value = false;
            editedContent.value = "";
            yamlError.value = null;
        });
        function toggleEdit() {
            if (isEditing.value) {
                cancelEdit();
            }
            else {
                startEdit();
            }
        }
        function startEdit() {
            editedContent.value = getReadmeContent.value || "";
            yamlError.value = null;
            isEditing.value = true;
        }
        function cancelEdit() {
            isEditing.value = false;
            editedContent.value = "";
            yamlError.value = null;
        }
        function validateYaml() {
            try {
                yaml.parse(editedContent.value);
                yamlError.value = null;
            }
            catch (e) {
                yamlError.value = e instanceof Error ? e.message : "Invalid YAML";
            }
        }
        async function saveReadme() {
            const uri = store.current_dataset?.uri;
            if (!uri || yamlError.value)
                return;
            isSaving.value = true;
            try {
                const response = await dserverApi.setReadme(uri, editedContent.value);
                // Ignore the response if the user has switched datasets in the meantime
                if (store.current_dataset?.uri !== uri)
                    return;
                // Update store with new readme
                store.updateCurrentDatasetReadme(response);
                isEditing.value = false;
            }
            catch (err) {
                console.error("Failed to save README:", err);
                yamlError.value = "Failed to save README. Please try again.";
            }
            finally {
                isSaving.value = false;
            }
        }
        function formatLine(line) {
            // Escape HTML first
            const escaped = line
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            // Match YAML-like patterns: "key: value" or "  key: value"
            const yamlMatch = escaped.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_-]*):\s*(.*)$/);
            if (yamlMatch) {
                const [, indent, key, value] = yamlMatch;
                if (value) {
                    return `${indent}<span class="yaml-key">${key}:</span> <span class="yaml-value">${value}</span>`;
                }
                else {
                    return `${indent}<span class="yaml-key">${key}:</span>`;
                }
            }
            // Match list items: "- item" or "  - item"
            const listMatch = escaped.match(/^(\s*)-\s+(.*)$/);
            if (listMatch) {
                const [, indent, content] = listMatch;
                return `${indent}<span class="yaml-bullet">-</span> ${content}`;
            }
            // Match comments: "# comment"
            const commentMatch = escaped.match(/^(\s*)(#.*)$/);
            if (commentMatch) {
                const [, indent, comment] = commentMatch;
                return `${indent}<span class="yaml-comment">${comment}</span>`;
            }
            return escaped;
        }
        async function copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                notifications.success("Copied to clipboard");
            }
            catch (err) {
                console.error("Failed to copy:", err);
                notifications.error("Failed to copy to clipboard");
            }
        }
        return (_ctx, _cache) => {
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_textarea = _resolveComponent("v-textarea");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis" }, "Dataset README (YAML)", -1)),
                    (isValidYaml.value && getReadmeContent.value)
                        ? (_openBlock(), _createBlock(_component_v_btn, {
                            key: 0,
                            size: "small",
                            variant: "tonal",
                            color: isEditing.value ? 'grey' : 'primary',
                            "prepend-icon": isEditing.value ? 'mdi-close' : 'mdi-pencil',
                            onClick: toggleEdit
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(isEditing.value ? "Cancel" : "Edit"), 1)
                            ]),
                            _: 1
                        }, 8, ["color", "prepend-icon"]))
                        : (getReadmeContent.value)
                            ? (_openBlock(), _createBlock(_component_v_menu, {
                                key: 1,
                                location: "bottom end",
                                "close-on-content-click": false
                            }, {
                                activator: _withCtx(({ props }) => [
                                    _createVNode(_component_v_btn, _mergeProps(props, {
                                        size: "small",
                                        variant: "tonal",
                                        color: "primary",
                                        "prepend-icon": "mdi-pencil"
                                    }), {
                                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                                _createTextVNode(" Edit ", -1)
                                            ]))]),
                                        _: 1
                                    }, 16)
                                ]),
                                default: _withCtx(() => [
                                    _createVNode(_component_v_card, {
                                        "min-width": "440",
                                        rounded: "lg"
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                                        _createTextVNode(" Edit the README and update it in the dataset: ", -1)
                                                    ]))]),
                                                _: 1
                                            }),
                                            _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                        "model-value": edit_command.value,
                                                        readonly: "",
                                                        density: "compact",
                                                        variant: "outlined",
                                                        "hide-details": "",
                                                        rounded: "lg",
                                                        "bg-color": "grey-lighten-4"
                                                    }, {
                                                        "append-inner": _withCtx(() => [
                                                            _createVNode(_component_v_btn, {
                                                                icon: "mdi-content-copy",
                                                                size: "small",
                                                                variant: "text",
                                                                onClick: _cache[0] || (_cache[0] = ($event) => (copyToClipboard(edit_command.value)))
                                                            })
                                                        ]),
                                                        _: 1
                                                    }, 8, ["model-value"])
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                ]),
                (isEditing.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createVNode(_component_v_textarea, {
                            modelValue: editedContent.value,
                            "onUpdate:modelValue": [
                                _cache[1] || (_cache[1] = ($event) => ((editedContent).value = $event)),
                                validateYaml
                            ],
                            variant: "outlined",
                            density: "compact",
                            rows: "16",
                            "hide-details": "auto",
                            error: !!yamlError.value,
                            "error-messages": yamlError.value,
                            class: "yaml-editor-textarea"
                        }, null, 8, ["modelValue", "error", "error-messages"]),
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_component_v_btn, {
                                size: "small",
                                variant: "tonal",
                                color: "grey",
                                onClick: cancelEdit
                            }, {
                                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                        _createTextVNode(" Cancel ", -1)
                                    ]))]),
                                _: 1
                            }),
                            _createVNode(_component_v_btn, {
                                size: "small",
                                variant: "tonal",
                                color: "primary",
                                loading: isSaving.value,
                                disabled: !!yamlError.value,
                                onClick: saveReadme
                            }, {
                                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                        _createTextVNode(" Save ", -1)
                                    ]))]),
                                _: 1
                            }, 8, ["loading", "disabled"])
                        ])
                    ]))
                    : (getReadmeContent.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createElementVNode("pre", _hoisted_6, [
                                _createElementVNode("code", { innerHTML: formattedReadme.value }, null, 8, _hoisted_7)
                            ])
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createVNode(_component_v_icon, {
                                size: "48",
                                color: "grey-lighten-1",
                                class: "mb-2"
                            }, {
                                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                        _createTextVNode("mdi-file-document-outline", -1)
                                    ]))]),
                                _: 1
                            }),
                            _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-body-2" }, "No README content", -1))
                        ]))
            ]));
        };
    }
});
